import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import QuizIcon from "@mui/icons-material/Quiz";
import { NavLink } from "react-router-dom";
import axios from "axios";
import liff from "@line/liff";
import Header from "../components/common/Header";
import Footer from "../components/common/Footer";
import { ensureLiffInit } from "../liffClient";

type Question = {
  image_url: string;
  choices: string[];
  answer: string;
};

const LANG_LABELS: Record<string, string> = {
  english: "英語",
  french: "フランス語",
};

const QUESTION_COUNT = 5;

type Phase = "setup" | "playing" | "finished";

export default function Quiz() {
  const [languages, setLanguages] = useState<string[]>([]);
  const [lang, setLang] = useState("");
  const [phase, setPhase] = useState<Phase>("setup");
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState<Question | null>(null);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);

  useEffect(() => {
    axios
      .post("/v1/api/quiz/languages", {})
      .then((response) => {
        const fetched: string[] = response.data.languages ?? [];
        setLanguages(fetched);
        if (fetched.length > 0) setLang(fetched[0]);
      })
      .catch((error) => console.error("Error fetching quiz languages:", error));

    // ログイン状態の確認はあくまで背景で試みるだけで、失敗してもクイズ自体は続行できる
    ensureLiffInit().then((ok) => setLoggedIn(ok && liff.isLoggedIn()));
  }, []);

  const handleLogin = async () => {
    const ok = await ensureLiffInit();
    if (!ok) {
      setLoginFailed(true);
      return;
    }
    liff.login();
  };

  const fetchQuestion = async () => {
    setLoading(true);
    setSelected(null);
    try {
      const response = await axios.post("/v1/api/quiz/question", { lang });
      setCurrent(response.data);
    } catch (error) {
      console.error("Error fetching quiz question:", error);
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async () => {
    if (!lang) return;
    setScore(0);
    setQuestionNumber(1);
    setPhase("playing");
    await fetchQuestion();
  };

  const handleChoice = async (choice: string) => {
    if (selected || !current) return;
    setSelected(choice);

    const correct = choice === current.answer;
    if (correct) {
      setScore((s) => s + 1);
    } else if (loggedIn) {
      try {
        const profile = await liff.getProfile();
        await axios.post("/v1/api/quiz/mistake/record", {
          userId: profile.userId,
          word: current.answer,
          imageUrl: current.image_url,
        });
      } catch (error) {
        console.error("Error recording quiz mistake:", error);
      }
    }

    setTimeout(() => {
      if (questionNumber < QUESTION_COUNT) {
        setQuestionNumber((n) => n + 1);
        fetchQuestion();
      } else {
        setPhase("finished");
      }
    }, 900);
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f1f5f9", display: "flex", flexDirection: "column" }}>
      <Box sx={{ width: "100%", position: "sticky", top: "0", zIndex: 100 }}>
        <Header />
      </Box>

      <Box sx={{ maxWidth: 560, mx: "auto", px: 2, pt: 4, pb: 2, flex: 1, width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #4338ca, #6366f1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(99,102,241,0.4)",
            }}
          >
            <QuizIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#1e293b", lineHeight: 1.2 }}>
              画像クイズ
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>
              画像を見て単語を4択で当てよう
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            p: 2.5,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            border: "1px solid rgba(226,232,240,0.8)",
          }}
        >
          {phase === "setup" && (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#475569", mb: 1.5 }}>
                言語を選んでください
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={lang}
                onChange={(_e, value) => value && setLang(value)}
                sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}
              >
                {languages.map((l) => (
                  <ToggleButton
                    key={l}
                    value={l}
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px !important",
                      fontWeight: 600,
                      "&.Mui-selected": {
                        background: "linear-gradient(135deg, #4338ca, #6366f1)",
                        color: "white",
                        "&:hover": { background: "linear-gradient(135deg, #3730a3, #4f46e5)" },
                      },
                    }}
                  >
                    {LANG_LABELS[l] ?? l}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {!loggedIn && (
                <Typography sx={{ color: "#94a3b8", fontSize: "0.78rem", mb: 2 }}>
                  ログインなしで参加できます。ログインすると間違えた問題を後から見返せます。
                </Typography>
              )}

              <Button
                fullWidth
                disabled={loading || !lang}
                onClick={startQuiz}
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #4338ca, #6366f1)",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.5,
                  boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
                  "&:hover": { background: "linear-gradient(135deg, #3730a3, #4f46e5)" },
                }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "クイズを始める"}
              </Button>
            </Box>
          )}

          {phase === "playing" && loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#6366f1" }} />
            </Box>
          )}

          {phase === "playing" && !loading && current && (
            <Box>
              <Typography sx={{ color: "#94a3b8", fontSize: "0.8rem", fontWeight: 600, mb: 2 }}>
                {questionNumber} / {QUESTION_COUNT}
              </Typography>

              <Box
                component="img"
                src={current.image_url}
                alt="quiz"
                sx={{
                  width: "100%",
                  height: 220,
                  objectFit: "cover",
                  borderRadius: "14px",
                  mb: 3,
                  border: "2px solid #f1f5f9",
                }}
              />

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                {current.choices.map((choice) => {
                  const isSelected = selected === choice;
                  const isAnswer = choice === current.answer;
                  const showResult = Boolean(selected);
                  let background = "#f8fafc";
                  let color = "#1e293b";
                  let border = "1px solid #e2e8f0";
                  if (showResult && isAnswer) {
                    background = "#dcfce7";
                    color = "#166534";
                    border = "1px solid #86efac";
                  } else if (showResult && isSelected && !isAnswer) {
                    background = "#fee2e2";
                    color = "#991b1b";
                    border = "1px solid #fca5a5";
                  }
                  return (
                    <Button
                      key={choice}
                      onClick={() => handleChoice(choice)}
                      disabled={showResult}
                      sx={{
                        backgroundColor: background,
                        color,
                        border,
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "1rem",
                        py: 1.75,
                        "&:hover": { backgroundColor: showResult ? background : "#eef2ff" },
                        "&.Mui-disabled": { color, backgroundColor: background, borderColor: border },
                      }}
                    >
                      {choice}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          )}

          {phase === "finished" && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography sx={{ fontSize: "0.9rem", color: "#64748b", fontWeight: 600, mb: 1 }}>
                結果
              </Typography>
              <Typography sx={{ fontSize: "2.2rem", fontWeight: 800, color: "#1e293b", mb: 3 }}>
                {score} / {QUESTION_COUNT}
              </Typography>

              <Button
                fullWidth
                onClick={() => setPhase("setup")}
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #4338ca, #6366f1)",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.5,
                  mb: 1.5,
                  boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
                  "&:hover": { background: "linear-gradient(135deg, #3730a3, #4f46e5)" },
                }}
              >
                もう一度挑戦する
              </Button>

              {loggedIn ? (
                <Button
                  fullWidth
                  component={NavLink}
                  to="/quiz/mistakes"
                  sx={{ color: "#4338ca", textTransform: "none", fontWeight: 600 }}
                >
                  間違えた問題を見る
                </Button>
              ) : (
                <Button
                  fullWidth
                  onClick={handleLogin}
                  sx={{ color: "#4338ca", textTransform: "none", fontWeight: 600 }}
                >
                  ログインして間違えた問題を記録する
                </Button>
              )}

              {loginFailed && (
                <Typography sx={{ color: "#ef4444", fontSize: "0.78rem", mt: 1.5 }}>
                  ログインに接続できませんでした。時間をおいて再度お試しください。
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}
